// @ts-nocheck
import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { Bell, Mail, MessageSquare, Lightbulb, CheckCircle, Settings, Loader2 } from 'lucide-react';
import { useWorkspace } from '@/contexts/context';
import { supabase } from '@product-radar/shared';
import { storeSlackOAuthState, getWorkspaceIdFromSlackState } from '@/lib/slack-oauth';

type NotificationFrequency = 'immediate' | 'daily' | 'weekly';

interface NotificationPrefs {
  feedback: { email: boolean; slack: boolean; frequency: NotificationFrequency };
  ideas: { email: boolean; slack: boolean; frequency: NotificationFrequency };
}

const DEFAULT_PREFS: NotificationPrefs = {
  feedback: { email: true, slack: false, frequency: 'daily' },
  ideas: { email: true, slack: false, frequency: 'daily' },
};

const SLACK_SCOPES = 'chat:write,channels:join,channels:history,channels:read,groups:history,groups:read,users:read,commands';

export function Notifications() {
  const { workspace, membership, refreshWorkspace, userProfile } = useWorkspace();
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [slackConnected, setSlackConnected] = useState(false);
  const [slackChannelId, setSlackChannelId] = useState<string | null>(null);
  const [slackChannels, setSlackChannels] = useState<{ id: string; name: string; isPrivate: boolean }[]>([]);
  const [slackChannelsLoading, setSlackChannelsLoading] = useState(false);
  const [slackChannelSaving, setSlackChannelSaving] = useState(false);
  const [oauthInProgress, setOauthInProgress] = useState(false);
  const [prefs, setPrefs] = useState<NotificationPrefs>(DEFAULT_PREFS);
  const [isSaving, setIsSaving] = useState(false);
  const lastWorkspaceIdRef = useRef<string | null>(null);

  const fetchSlackStatus = useCallback(
    async (forWorkspaceId?: string) => {
      const id = forWorkspaceId ?? workspace?.id;
      if (!id) return;
      const { data } = await supabase.rpc('get_slack_connection_status', { p_workspace_id: id });
      const row = Array.isArray(data) ? data[0] : data;
      const connected = !!row?.slack_team_id;
      const channelId = row?.slack_channel_id || null;
      if (id === workspace?.id) {
        setSlackConnected(connected);
        setSlackChannelId(channelId);
      }
    },
    [workspace?.id]
  );

  useEffect(() => {
    if (!workspace) return;
    fetchSlackStatus();
  }, [workspace, fetchSlackStatus]);

  // Handle Slack OAuth callback (code + state in URL)
  const oauthProcessedRef = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state || oauthProcessedRef.current) return;

    const workspaceId = getWorkspaceIdFromSlackState(state);
    if (!workspaceId) {
      toast.error('Slack connection expired or invalid. Please try again.');
      window.history.replaceState({}, '', window.location.pathname || '/');
      return;
    }

    oauthProcessedRef.current = true;

    const exchangeAndSave = async () => {
      setOauthInProgress(true);
      try {
        const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
        const fnUrl = `https://${projectId}.supabase.co/functions/v1/slack-oauth-callback`;
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          toast.error('Please sign in to connect Slack');
          oauthProcessedRef.current = false;
          return;
        }
        const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
        const redirectUri = `${appUrl.replace(/\/$/, '')}/settings`;
        const res = await fetch(fnUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ code, state, workspace_id: workspaceId, redirect_uri: redirectUri }),
        });
        const result = await res.json();
        if (!res.ok) throw new Error(result.error || 'Failed to connect Slack');

        await fetchSlackStatus(workspaceId);
        toast.success('Slack connected!', { description: 'Select a channel to receive notifications.' });
        window.history.replaceState({}, '', window.location.pathname || '/');
      } catch (e: any) {
        toast.error('Failed to connect Slack', { description: e?.message });
        oauthProcessedRef.current = false;
      } finally {
        setOauthInProgress(false);
      }
    };

    exchangeAndSave();
  }, [fetchSlackStatus]);

  useEffect(() => {
    if (!workspace) return;
    const raw = membership?.notification_preferences ?? workspace.settings?.notification_preferences;
    if (raw && typeof raw === 'object') {
      setPrefs({
        feedback: {
          email: raw.feedback?.email ?? DEFAULT_PREFS.feedback.email,
          slack: raw.feedback?.slack ?? DEFAULT_PREFS.feedback.slack,
          frequency: (raw.feedback?.frequency || 'daily') as NotificationFrequency,
        },
        ideas: {
          email: raw.ideas?.email ?? DEFAULT_PREFS.ideas.email,
          slack: raw.ideas?.slack ?? DEFAULT_PREFS.ideas.slack,
          frequency: (raw.ideas?.frequency || 'daily') as NotificationFrequency,
        },
      });
    }
    if (lastWorkspaceIdRef.current !== workspace.id) {
      lastWorkspaceIdRef.current = workspace.id;
    }
  }, [workspace, membership]);

  const updatePref = (
    category: 'feedback' | 'ideas',
    key: keyof NotificationPrefs['feedback'],
    value: boolean | NotificationFrequency
  ) => {
    setPrefs((prev) => ({
      ...prev,
      [category]: { ...prev[category], [key]: value },
    }));
  };

  const handleSave = async () => {
    if (!workspace) return;

    setIsSaving(true);
    try {
      const { error } = await supabase.rpc('update_member_notification_preferences', {
        p_workspace_id: workspace.id,
        p_prefs: prefs,
      });

      if (error) throw error;

      await refreshWorkspace();
      toast.success('Notification settings saved!', {
        description: 'Your notification preferences have been updated',
      });
    } catch (e: any) {
      console.error('Error saving notification preferences:', e);
      toast.error('Failed to save settings', { description: e?.message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddToSlack = () => {
    const clientId = import.meta.env.VITE_PUBLIC_SLACK_CLIENT_ID;
    const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
    if (!clientId || !workspace) return;
    const redirectUri = `${appUrl.replace(/\/$/, '')}/settings`;
    const state = storeSlackOAuthState(workspace.id);
    window.location.href = `https://slack.com/oauth/v2/authorize?client_id=${clientId}&scope=${encodeURIComponent(SLACK_SCOPES)}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${encodeURIComponent(state)}`;
  };

  const handleLoadChannels = async () => {
    if (!workspace) return;
    setSlackChannelsLoading(true);
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const fnUrl = `https://${projectId}.supabase.co/functions/v1/slack-channels`;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const res = await fetch(fnUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ workspace_id: workspace.id }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to load channels');
      setSlackChannels(result.channels || []);
    } catch (e: any) {
      toast.error('Failed to load Slack channels', { description: e?.message });
    } finally {
      setSlackChannelsLoading(false);
    }
  };

  const handleChannelChange = async (channelId: string) => {
    if (!workspace) return;
    setSlackChannelSaving(true);
    try {
      const { error } = await supabase.rpc('update_slack_notification_channel', {
        p_workspace_id: workspace.id,
        p_slack_channel_id: channelId || null,
      });
      if (error) throw error;
      setSlackChannelId(channelId || null);
      toast.success('Notification channel updated');
    } catch (e: any) {
      toast.error('Failed to update channel', { description: e?.message });
    } finally {
      setSlackChannelSaving(false);
    }
  };

  useEffect(() => {
    if (slackConnected && slackChannels.length === 0 && !slackChannelsLoading) {
      handleLoadChannels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slackConnected]);

  const categories = [
    {
      id: 'feedback' as const,
      name: 'Feedback',
      description: 'New feedback submissions, status changes, and comments',
      icon: <MessageSquare className="h-5 w-5" />,
    },
    {
      id: 'ideas' as const,
      name: 'Ideas',
      description: 'New ideas, status updates, and priority changes',
      icon: <Lightbulb className="h-5 w-5" />,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1>Notifications</h1>
        <p className="text-muted-foreground">Manage how and when you receive notifications</p>
      </div>

      {/* Notification Channels */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Bell className="h-5 w-5" />
            <CardTitle>Notification Channels</CardTitle>
          </div>
          <CardDescription>Configure your preferred notification methods</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Mail className="h-5 w-5 text-muted-foreground" />
              <div>
                <Label>Email Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive notifications via {userProfile?.email || 'your email'}
                </p>
              </div>
            </div>
            <Switch checked={emailNotifications} onCheckedChange={setEmailNotifications} />
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <MessageSquare className="h-5 w-5 text-muted-foreground" />
              <div>
                <Label>Slack Integration</Label>
                <p className="text-sm text-muted-foreground">Send notifications to Slack channels</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {slackConnected ? (
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-muted-foreground">Connected</span>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddToSlack}
                  disabled={!import.meta.env.VITE_PUBLIC_SLACK_CLIENT_ID || oauthInProgress}
                >
                  {oauthInProgress ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add to Slack'}
                </Button>
              )}
            </div>
          </div>

          {slackConnected && (
            <div className="ml-8 space-y-3 p-3 bg-muted/50 rounded-lg">
              <div className="space-y-2">
                <Label className="text-sm">Notification channel</Label>
                <Select
                  value={slackChannelId || '_none'}
                  onValueChange={(v) => handleChannelChange(v === '_none' ? '' : v)}
                  disabled={slackChannelSaving}
                >
                  <SelectTrigger className="max-w-xs">
                    <SelectValue placeholder={slackChannelsLoading ? 'Loading...' : 'Select a channel'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">No channel selected</SelectItem>
                    {slackChannels.map((ch) => (
                      <SelectItem key={ch.id} value={ch.id}>
                        #{ch.name} {ch.isPrivate ? '(private)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  New feedback and ideas will be posted to this channel when enabled below.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notification Preferences */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Settings className="h-5 w-5" />
            <CardTitle>Notification Preferences</CardTitle>
          </div>
          <CardDescription>Choose when to receive notifications for each type</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {categories.map((category) => (
              <div key={category.id} className="space-y-4">
                <div className="flex items-center space-x-3">
                  {category.icon}
                  <div>
                    <h4 className="font-medium">{category.name}</h4>
                    <p className="text-sm text-muted-foreground">{category.description}</p>
                  </div>
                </div>

                <div className="ml-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex min-h-10 items-center gap-3">
                    <Label className="text-sm">Email</Label>
                    <Switch
                      checked={prefs[category.id].email}
                      onCheckedChange={(checked) => updatePref(category.id, 'email', checked)}
                      disabled={!emailNotifications}
                    />
                  </div>
                  <div className="flex min-h-10 items-center gap-3">
                    <Label className="text-sm">Slack</Label>
                    <Switch
                      checked={prefs[category.id].slack}
                      onCheckedChange={(checked) => updatePref(category.id, 'slack', checked)}
                      disabled={!slackConnected || !slackChannelId}
                    />
                  </div>
                  <div className="flex min-h-10 flex-col justify-center space-y-1">
                    <Label className="text-sm">Frequency</Label>
                    <Select
                      value={prefs[category.id].frequency}
                      onValueChange={(value) =>
                        updatePref(category.id, 'frequency', value as NotificationFrequency)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="immediate">Immediate</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {category.id !== categories[categories.length - 1].id && <Separator />}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}
